import React, { FC, ReactNode, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { NewRequestForm } from "@/components/pages/requests/NewRequestForm";
import { CreateRequest, RequestType } from "@/types/request";
import { useCreateRequest } from "@/components/pages/requests/hooks/useRequests";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getCreateRequestSchemaByType } from "@/components/pages/requests/utils/requestSchema";
import { useHotel } from "@/hooks/useHotel";
import { X } from "lucide-react";

interface CreateRequestModalProps {
  requestType: RequestType;
  triggerNode: ReactNode;
  bookingId?: string;
  guestId?: string;
  roomId?: string;
  initialGuestDisplay?: string;
  initialReservationDisplay?: string;
  initialRoomDisplay?: string;
  onSuccess?: (requestId: string) => void;
}

const CreateRequestModal: FC<CreateRequestModalProps> = ({
  requestType,
  triggerNode,
  bookingId,
  guestId,
  roomId,
  initialGuestDisplay,
  initialReservationDisplay,
  initialRoomDisplay,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const createRequestMutation = useCreateRequest();

  const handleRequestCreated = async (newRequest: CreateRequest) => {
    try {
      const schema = getCreateRequestSchemaByType(newRequest.type);
      const result = schema.safeParse(newRequest);
      if (!result.success) {
        const firstError =
          result.error.issues?.[0]?.message ||
          t("requests.form.pleaseFillinAllRequiredFields");
        toast.error(firstError);
        return;
      }

      const createRequestPayload = {
        ...newRequest,
        roomId: newRequest.roomId,
        hotelId,
      };

      const createdRequest =
        await createRequestMutation.mutateAsync(createRequestPayload);
      toast.success(t("requests.form.requestCreatedSuccessfully"));
      setIsModalOpen(false);
      if (createdRequest?.id) {
        onSuccess?.(createdRequest.id);
      }
    } catch (error) {
      toast.error(t("requests.form.failedToCreateRequest"));
      console.error(t("requests.page.failedToCreateRequest"), error);
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogTrigger
        asChild
        onClick={(event) => {
          event.stopPropagation();
        }}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
      >
        {triggerNode}
      </DialogTrigger>
      <DialogOverlay />
      <DialogContent
        showCloseButton={false}
        className="border-base-border flex max-h-[min(860px,calc(100vh-48px))] w-[640px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-lg sm:max-w-[640px]"
        onClick={(event) => {
          event.stopPropagation();
        }}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
      >
        <DialogHeader className="border-base-border flex-row items-center justify-between border-b px-6 py-5 text-left">
          <DialogTitle className="text-lg leading-normal font-semibold text-black/96">
            {t("requests.form.createNewRequest")}
          </DialogTitle>
          <DialogClose asChild>
            <Button
              type="button"
              variant="new_ghost"
              size="icon_sm"
              aria-label={t("requests.form.close")}
            >
              <X />
            </Button>
          </DialogClose>
        </DialogHeader>
        {isModalOpen ? (
          <NewRequestForm
            requestType={requestType}
            onClose={() => setIsModalOpen(false)}
            onSuccess={handleRequestCreated}
            initialBookingId={bookingId}
            initialGuestId={guestId}
            initialRoomId={roomId}
            initialGuestDisplay={initialGuestDisplay}
            initialReservationDisplay={initialReservationDisplay}
            initialRoomDisplay={initialRoomDisplay}
            isSubmitting={createRequestMutation.isPending}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CreateRequestModal;
